<template>
  <ContentWrap title="维修工单管理">
    <!-- 搜索工作栏 -->
    <el-form :model="queryParams" ref="queryFormRef" :inline="true" label-width="68px" class="-mb-15px">
      <el-form-item label="店铺" prop="shopId">
        <el-select
          v-model="queryParams.shopId"
          placeholder="请选择店铺"
          clearable
          filterable
          class="!w-160px"
          @change="handleQuery"
        >
          <el-option v-for="item in shopList" :key="item.id" :label="item.name" :value="item.id" />
        </el-select>
      </el-form-item>
      <el-form-item label="房间" prop="roomId">
        <el-select
          v-model="queryParams.roomId"
          placeholder="请选择房间"
          clearable
          class="!w-180px"
        >
          <el-option
            v-for="r in roomOptions"
            :key="r.id"
            :label="r.roomNo || '#' + r.id"
            :value="r.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="工单号" prop="orderNo">
        <el-input
          v-model="queryParams.orderNo"
          placeholder="请输入工单号"
          clearable
          class="!w-180px"
          @keyup.enter="handleQuery"
        />
      </el-form-item>
      <el-form-item label="故障类型" prop="faultType">
        <el-select
          v-model="queryParams.faultType"
          placeholder="请选择类型"
          clearable
          class="!w-160px"
        >
          <el-option label="水电故障" value="WATER_ELECTRIC" />
          <el-option label="空调故障" value="AIR_CONDITIONER" />
          <el-option label="门窗故障" value="DOOR_WINDOW" />
          <el-option label="家具故障" value="FURNITURE" />
          <el-option label="网络故障" value="NETWORK" />
          <el-option label="其他" value="OTHER" />
        </el-select>
      </el-form-item>
      <el-form-item label="优先级" prop="priority">
        <el-select
          v-model="queryParams.priority"
          placeholder="请选择优先级"
          clearable
          class="!w-140px"
        >
          <el-option label="低" :value="0" />
          <el-option label="中" :value="1" />
          <el-option label="高" :value="2" />
          <el-option label="紧急" :value="3" />
        </el-select>
      </el-form-item>
      <el-form-item label="状态" prop="status">
        <el-select
          v-model="queryParams.status"
          placeholder="请选择状态"
          clearable
          class="!w-140px"
        >
          <el-option label="待派单" :value="0" />
          <el-option label="已派单" :value="1" />
          <el-option label="维修中" :value="2" />
          <el-option label="待验收" :value="3" />
          <el-option label="已完成" :value="4" />
          <el-option label="已取消" :value="5" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button @click="handleQuery"><Icon icon="ep:search" class="mr-5px" /> 搜索</el-button>
        <el-button @click="resetQuery"><Icon icon="ep:refresh" class="mr-5px" /> 重置</el-button>
        <el-button
          type="primary"
          plain
          @click="openForm('create')"
          v-hasPermi="['hotel:maintenance:create']"
        >
          <Icon icon="ep:plus" class="mr-5px" /> 新增报修
        </el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <!-- 工单列表 -->
  <ContentWrap>
    <el-table v-loading="loading" :data="list" :stripe="true" border>
      <el-table-column label="工单号" align="center" prop="orderNo" width="160" />
      <el-table-column label="房间号" align="center" prop="roomNo" width="120" />
      <el-table-column label="故障类型" align="center" prop="faultType" width="120">
        <template #default="scope">
          <span>{{ getFaultTypeLabel(scope.row.faultType) }}</span>
        </template>
      </el-table-column>
      <el-table-column label="故障描述" align="center" prop="faultDesc" min-width="150">
        <template #default="scope">
          <span>{{ scope.row.faultDesc || '-' }}</span>
        </template>
      </el-table-column>
      <el-table-column label="优先级" align="center" prop="priority" width="90">
        <template #default="scope">
          <el-tag :type="getPriorityTag(scope.row.priority)">
            {{ getPriorityLabel(scope.row.priority) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="状态" align="center" prop="status" width="100">
        <template #default="scope">
          <el-tag :type="getStatusTag(scope.row.status)">
            {{ getStatusLabel(scope.row.status) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="负责人" align="center" prop="assigneeName" width="100">
        <template #default="scope">
          <span>{{ scope.row.assigneeName || '-' }}</span>
        </template>
      </el-table-column>
      <el-table-column label="维修费用" align="center" prop="cost" width="120">
        <template #default="scope">
          <span>{{ scope.row.cost != null ? '¥' + scope.row.cost.toFixed(2) : '-' }}</span>
        </template>
      </el-table-column>
      <el-table-column label="验收人" align="center" prop="verifyUser" width="100">
        <template #default="scope">
          <span>{{ scope.row.verifyUser || '-' }}</span>
        </template>
      </el-table-column>
      <el-table-column label="创建时间" align="center" prop="createTime" width="160">
        <template #default="scope">
          <span>{{ scope.row.createTime || '-' }}</span>
        </template>
      </el-table-column>
      <el-table-column label="操作" align="center" width="280" fixed="right">
        <template #default="scope">
          <!-- 状态0: 待派单 → 派单 -->
          <el-button
            v-if="scope.row.status === 0"
            link
            type="primary"
            @click="openAssignDialog(scope.row)"
            v-hasPermi="['hotel:maintenance:update']"
          >
            派单
          </el-button>
          <!-- 状态1: 已派单 → 开始维修 -->
          <el-button
            v-if="scope.row.status === 1"
            link
            type="success"
            @click="handleStart(scope.row.id)"
            v-hasPermi="['hotel:maintenance:update']"
          >
            开始维修
          </el-button>
          <!-- 状态2: 维修中 → 完成维修 -->
          <el-button
            v-if="scope.row.status === 2"
            link
            type="warning"
            @click="openCompleteDialog(scope.row)"
            v-hasPermi="['hotel:maintenance:update']"
          >
            完成维修
          </el-button>
          <!-- 状态3: 待验收 → 验收 -->
          <el-button
            v-if="scope.row.status === 3"
            link
            type="success"
            @click="openVerifyDialog(scope.row)"
            v-hasPermi="['hotel:maintenance:verify']"
          >
            验收
          </el-button>
          <el-button
            link
            type="danger"
            @click="handleDelete(scope.row.id)"
            v-hasPermi="['hotel:maintenance:delete']"
          >
            删除
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      :total="total"
      v-model:page="queryParams.pageNo"
      v-model:limit="queryParams.pageSize"
      @pagination="getList"
    />
  </ContentWrap>

  <!-- 新增报修弹窗 -->
  <Dialog :title="'新增报修工单'" v-model="dialogVisible" width="550px">
    <el-form ref="formRef" :model="formData" :rules="formRules" label-width="100px" v-loading="formLoading">
      <el-form-item label="店铺" prop="shopId">
        <el-select
          v-model="formData.shopId"
          placeholder="请选择店铺"
          clearable
          filterable
          class="!w-1/1"
        >
          <el-option
            v-for="shop in shopList"
            :key="shop.id"
            :label="shop.name"
            :value="shop.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="房间" prop="roomId">
        <el-select
          v-model="formData.roomId"
          placeholder="请选择房间"
          class="!w-1/1"
        >
          <el-option
            v-for="r in roomOptions"
            :key="r.id"
            :label="r.roomNo || '#' + r.id"
            :value="r.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="故障类型" prop="faultType">
        <el-select v-model="formData.faultType" placeholder="请选择故障类型" class="!w-1/1">
          <el-option label="水电故障" value="WATER_ELECTRIC" />
          <el-option label="空调故障" value="AIR_CONDITIONER" />
          <el-option label="门窗故障" value="DOOR_WINDOW" />
          <el-option label="家具故障" value="FURNITURE" />
          <el-option label="网络故障" value="NETWORK" />
          <el-option label="其他" value="OTHER" />
        </el-select>
      </el-form-item>
      <el-form-item label="故障描述" prop="faultDesc">
        <el-input v-model="formData.faultDesc" type="textarea" :rows="3" placeholder="请输入故障描述" />
      </el-form-item>
      <el-form-item label="优先级" prop="priority">
        <el-select v-model="formData.priority" placeholder="请选择优先级" class="!w-1/1">
          <el-option label="低" :value="0" />
          <el-option label="中" :value="1" />
          <el-option label="高" :value="2" />
          <el-option label="紧急" :value="3" />
        </el-select>
      </el-form-item>
      <el-form-item label="备注" prop="remark">
        <el-input v-model="formData.remark" type="textarea" placeholder="请输入备注" />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="submitForm" type="primary" :disabled="formLoading">确 定</el-button>
      <el-button @click="dialogVisible = false">取 消</el-button>
    </template>
  </Dialog>

  <!-- 派单弹窗 -->
  <Dialog :title="'派单'" v-model="assignDialogVisible" width="450px">
    <el-form ref="assignFormRef" :model="assignFormData" :rules="assignFormRules" label-width="100px" v-loading="assignFormLoading">
      <el-form-item label="维修工" prop="assigneeId">
        <el-select v-model="assignFormData.assigneeId" placeholder="请选择维修工" filterable @change="onAssigneeChange">
          <el-option v-for="item in employeeList" :key="item.id" :label="item.name" :value="item.id" />
        </el-select>
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="submitAssign" type="primary" :disabled="assignFormLoading">确 定</el-button>
      <el-button @click="assignDialogVisible = false">取 消</el-button>
    </template>
  </Dialog>

  <!-- 完成维修弹窗 -->
  <Dialog :title="'完成维修'" v-model="completeDialogVisible" width="450px">
    <el-form ref="completeFormRef" :model="completeFormData" :rules="completeFormRules" label-width="100px" v-loading="completeFormLoading">
      <el-form-item label="维修费用" prop="cost">
        <el-input-number v-model="completeFormData.cost" :min="0" :precision="2" class="!w-1/1" placeholder="请输入维修费用" />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="submitComplete" type="primary" :disabled="completeFormLoading">确 定</el-button>
      <el-button @click="completeDialogVisible = false">取 消</el-button>
    </template>
  </Dialog>

  <!-- 验收弹窗 -->
  <Dialog :title="'验收维修工单'" v-model="verifyDialogVisible" width="450px">
    <el-form ref="verifyFormRef" :model="verifyFormData" :rules="verifyFormRules" label-width="100px" v-loading="verifyFormLoading">
      <el-form-item label="验收人" prop="verifyUser">
        <el-select v-model="verifyFormData.verifyUser" placeholder="请选择验收人" filterable>
          <el-option v-for="item in employeeList" :key="item.id" :label="item.name" :value="item.name" />
        </el-select>
      </el-form-item>
      <el-form-item label="验收备注" prop="verifyRemark">
        <el-input v-model="verifyFormData.verifyRemark" type="textarea" placeholder="请输入验收备注" />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="submitVerify" type="primary" :disabled="verifyFormLoading">确 定</el-button>
      <el-button @click="verifyDialogVisible = false">取 消</el-button>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import * as maintenanceApi from '@/api/hotel/maintenance'
import * as roomApi from '@/api/hotel/room'
import { getShopSimpleList } from '@/api/hotel/platformShop'
import { getSpuSimpleList } from '@/api/mall/product/spu'
import { getEmployeeListAll } from '@/api/hotel/employee'

defineOptions({ name: 'HotelMaintenance' })

const message = useMessage()

const loading = ref(true)
const shopList = ref<{ id: string | number; name: string }[]>([])

// 员工下拉列表
const employeeList = ref<any[]>([])
const getEmployeeList = async () => {
  const data = await getEmployeeListAll()
  employeeList.value = data || []
}

const fetchDropdownData = async () => {
  try {
    const shopRes = await getShopSimpleList()
    shopList.value = shopRes || []
  } catch {
    // 静默处理
  }
}
const list = ref<maintenanceApi.MaintenanceVO[]>([])
const total = ref(0)
const queryParams = reactive({
  pageNo: 1,
  pageSize: 10,
  shopId: undefined,
  roomId: undefined,
  orderNo: undefined,
  faultType: undefined,
  priority: undefined,
  status: undefined
})
const queryFormRef = ref()

const faultTypeMap: Record<string, string> = {
  WATER_ELECTRIC: '水电故障',
  AIR_CONDITIONER: '空调故障',
  DOOR_WINDOW: '门窗故障',
  FURNITURE: '家具故障',
  NETWORK: '网络故障',
  OTHER: '其他'
}
const getFaultTypeLabel = (type: string) => faultTypeMap[type] || type || '-'

const priorityMap: Record<number, string> = { 0: '低', 1: '中', 2: '高', 3: '紧急' }
const priorityTagMap: Record<number, string> = { 0: 'info', 1: 'warning', 2: 'danger', 3: 'danger' }
const getPriorityLabel = (p: number) => priorityMap[p] || '未知'
const getPriorityTag = (p: number) => priorityTagMap[p] || 'info'

const statusMap: Record<number, string> = { 0: '待派单', 1: '已派单', 2: '维修中', 3: '待验收', 4: '已完成', 5: '已取消' }
const statusTagMap: Record<number, string> = { 0: 'info', 1: 'warning', 2: 'primary', 3: '', 4: 'success', 5: 'info' }
const getStatusLabel = (s: number) => statusMap[s] || '未知'
const getStatusTag = (s: number) => statusTagMap[s] || 'info'

const getList = async () => {
  loading.value = true
  try {
    const data = await maintenanceApi.getMaintenancePage(queryParams)
    list.value = data.list
    total.value = data.total
  } finally {
    loading.value = false
  }
}

const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

const resetQuery = () => {
  queryFormRef.value?.resetFields()
  handleQuery()
}

// 新增报修
const dialogVisible = ref(false)
const formLoading = ref(false)
const formRef = ref()
const formData = ref({
  shopId: undefined,
  roomId: undefined,
  faultType: '',
  faultDesc: '',
  priority: 1,
  remark: ''
})
const formRules = reactive({
  shopId: [{ required: true, message: '请选择店铺', trigger: 'change' }],
  roomId: [{ required: true, message: '请选择房间', trigger: 'change' }],
  faultType: [{ required: true, message: '请选择故障类型', trigger: 'change' }],
  priority: [{ required: true, message: '请选择优先级', trigger: 'change' }]
})

const openForm = (type: string) => {
  dialogVisible.value = true
  formData.value = {
    shopId: undefined,
    roomId: undefined,
    faultType: '',
    faultDesc: '',
    priority: 1,
    remark: ''
  }
  formRef.value?.resetFields()
}

const submitForm = async () => {
  await formRef.value.validate()
  formLoading.value = true
  try {
    const data = { ...formData.value } as any
    await maintenanceApi.createMaintenance(data)
    message.success('新增成功')
    dialogVisible.value = false
    await getList()
  } finally {
    formLoading.value = false
  }
}

const handleDelete = async (id: string | number) => {
  try {
    await message.delConfirm()
    await maintenanceApi.deleteMaintenance(id)
    message.success('删除成功')
    await getList()
  } catch {}
}

// 派单
const assignDialogVisible = ref(false)
const assignFormLoading = ref(false)
const assignFormRef = ref()
const currentOrderId = ref<string | number>()
const assignFormData = reactive({
  assigneeId: undefined as string | number | undefined,
  assigneeName: ''
})
const assignFormRules = reactive({
  assigneeId: [{ required: true, message: '请选择维修工', trigger: 'change' }]
})

// 选择维修工时联动赋值 assigneeName
const onAssigneeChange = (val: any) => {
  const emp = employeeList.value.find((e: any) => e.id === val)
  assignFormData.assigneeName = emp ? emp.name : ''
}

const openAssignDialog = (row: maintenanceApi.MaintenanceVO) => {
  currentOrderId.value = row.id
  assignFormData.assigneeId = undefined
  assignFormData.assigneeName = ''
  assignFormRef.value?.resetFields()
  assignDialogVisible.value = true
}

const submitAssign = async () => {
  await assignFormRef.value.validate()
  assignFormLoading.value = true
  try {
    await maintenanceApi.assign({
      id: currentOrderId.value!,
      assigneeId: assignFormData.assigneeId!,
      assigneeName: assignFormData.assigneeName
    })
    message.success('派单成功')
    assignDialogVisible.value = false
    await getList()
  } finally {
    assignFormLoading.value = false
  }
}

// 开始维修
const handleStart = async (id: string | number) => {
  try {
    await message.confirm('确认开始维修？')
    await maintenanceApi.start(id)
    message.success('已开始维修')
    await getList()
  } catch {}
}

// 完成维修
const completeDialogVisible = ref(false)
const completeFormLoading = ref(false)
const completeFormRef = ref()
const completeFormData = reactive({
  cost: 0
})
const completeFormRules = reactive({
  cost: [{ required: true, message: '请输入维修费用', trigger: 'blur' }]
})

const openCompleteDialog = (row: maintenanceApi.MaintenanceVO) => {
  currentOrderId.value = row.id
  completeFormData.cost = 0
  completeFormRef.value?.resetFields()
  completeDialogVisible.value = true
}

const submitComplete = async () => {
  await completeFormRef.value.validate()
  completeFormLoading.value = true
  try {
    await maintenanceApi.complete({
      id: currentOrderId.value!,
      cost: completeFormData.cost
    })
    message.success('维修完成')
    completeDialogVisible.value = false
    await getList()
  } finally {
    completeFormLoading.value = false
  }
}

// 验收
const verifyDialogVisible = ref(false)
const verifyFormLoading = ref(false)
const verifyFormRef = ref()
const verifyFormData = reactive({
  verifyUser: '',
  verifyRemark: ''
})
const verifyFormRules = reactive({
  verifyUser: [{ required: true, message: '请选择验收人', trigger: 'change' }]
})

const openVerifyDialog = (row: maintenanceApi.MaintenanceVO) => {
  currentOrderId.value = row.id
  verifyFormData.verifyUser = ''
  verifyFormData.verifyRemark = ''
  verifyFormRef.value?.resetFields()
  verifyDialogVisible.value = true
}

const submitVerify = async () => {
  await verifyFormRef.value.validate()
  verifyFormLoading.value = true
  try {
    await maintenanceApi.verify({
      id: currentOrderId.value!,
      verifyUser: verifyFormData.verifyUser,
      verifyRemark: verifyFormData.verifyRemark
    })
    message.success('验收成功')
    verifyDialogVisible.value = false
    await getList()
  } finally {
    verifyFormLoading.value = false
  }
}

// 房间下拉选项
const roomOptions = ref<{ id: string | number; roomNo: string }[]>([])
const loadRoomOptions = async () => {
  const data = await roomApi.listAllRooms()
  roomOptions.value = data.list || []
}

onMounted(() => {
  fetchDropdownData()
  getList()
  loadRoomOptions()
  getEmployeeList()
})
</script>
